import { useEffect, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import RadioFilter from 'components/RadioFilter';
import { statusFilterOptions, filterValues } from './helper'
import { connect } from 'react-redux';
import { TreatmentPlansActions } from 'store/actions';
import toast from 'react-hot-toast';
import { debounce } from 'lodash';
import { List } from 'components/TreatmentPlan';

let enhancer = connect(null, { ...TreatmentPlansActions });

const TreatmentPlan = ({ getTreatmentPlans }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || "";
  const initialStatus = searchParams.get('status') || "all";

  const [treatmentPlans, setTreatmentPlans] = useState(null);
  const [pageEnd, setPageEnd] = useState(false)
  const [params, setParams] = useState({
    q: { title_cont: initialSearch, status: initialStatus },
    page: 1,
    items: 10
  });

  useEffect(() => {
    loadTreatmentPlans(params);
  }, []);

  const loadTreatmentPlans = async (_params) => {
    try {
      const { data: _treatmentPlans = [], pagy } = await getTreatmentPlans({
        ..._params,
        q: { title_cont: _params.q?.title_cont, ...filterValues[_params.q.status] }
      });

      setTreatmentPlans(_params.page == 1 ? _treatmentPlans : [...(treatmentPlans ?? []), ..._treatmentPlans])
      setParams(_params)
      setPageEnd(!pagy?.next)

    } catch (error) {
      toast.error(error.errors || 'Failed to load treatment plans');
      setTreatmentPlans([]);
    }
  };

  const updateUrlParams = (key, value) => {
    setSearchParams((prev) => {
      if (value && value !== 'all') {
        prev.set(key, value);
      } else {
        prev.delete(key);
      }
      return prev;
    });
  };

  const debouncedLoad = useCallback(
    debounce((searchValue: string) => {
      updateUrlParams('search', searchValue);
      setTreatmentPlans(null)
      loadTreatmentPlans({ ...params, page: 1, q: { ...params.q, title_cont: searchValue } });
    }, 800),
    [params, setSearchParams]
  );

  const loadMore = () => {
    loadTreatmentPlans({ ...params, page: params.page + 1 });
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Treatment Plans</h1>
        <p className="text-sm text-secondary-500 mt-0.5">Review and manage your treatment plans.</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 pointer-events-none"
          />
          <input
            type="text"
            defaultValue={initialSearch}
            placeholder="Search by treatment plan's title..."
            onChange={(e) => debouncedLoad(e.target.value)}
            className="input input-bordered w-full pl-9 rounded-xl text-sm h-10 focus:outline-none focus:border-blue-400"
          />
        </div>

        <div className="self-end sm:self-auto">
          <RadioFilter
            options={statusFilterOptions}
            value={params?.q?.status}
            onChange={(val) => {
              setTreatmentPlans(null)
              updateUrlParams('status', val);
              const newParams = { ...params, q: { ...params?.q, status: val } };
              setParams(newParams);
              loadTreatmentPlans({ ...newParams, page: 1 })
            }}
          />
        </div>
      </div>

      <List
        treatmentPlans={treatmentPlans}
        loadMore={loadMore}
        pageEnd={pageEnd}
      />
    </div>
  );
};

export default enhancer(TreatmentPlan)
